export class ScrollSlider {
	slider: HTMLElement;
	isMouseDown: boolean;
	dragStart: number | null;
	currentScrollPosition: number;
	sliderNavigationButtons: HTMLElement | null;
	paginationEl: HTMLElement | null = null;
	paginationDots: HTMLElement[] = [];

	private slides: HTMLElement[] = [];
	private prevBtn: HTMLButtonElement | null = null;
	private nextBtn: HTMLButtonElement | null = null;
	private intersectionObserver: IntersectionObserver | null = null;
	
	constructor(element: HTMLElement) {
		this.slider = element;
		this.isMouseDown = false;
		this.dragStart = null;
		this.currentScrollPosition = 0;
		this.slides = Array.from(this.slider.children) as HTMLElement[];

		const wrapper = this.slider.parentElement;
		this.sliderNavigationButtons = wrapper?.querySelector('.scroll-slider__navigation') ?? null;
		this.paginationEl = wrapper?.querySelector('.scroll-slider__pagination') ?? null;

		this.init();
	}

	init() {
		this.addCssScrollProperties();

		// Drag & Drop
		this.slider.addEventListener('dragstart', (event: DragEvent) => event.preventDefault());

		this.slider.addEventListener('mousedown', (event: MouseEvent) => {
			this.clearCssScrollProperties();
			this.isMouseDown = true;
			this.dragStart = event.clientX;
			this.currentScrollPosition = this.slider.scrollLeft;
		});

		this.slider.addEventListener('mouseup', () => this.resetMouseState());
		this.slider.addEventListener('mouseleave', () => this.resetMouseState());
		this.slider.addEventListener('mouseenter', () => this.resetMouseState());

		this.slider.addEventListener('mousemove', (event: MouseEvent) => {
			if (this.isMouseDown && this.dragStart !== null) {
				const deltaX = event.clientX - this.dragStart;
				event.preventDefault();
				this.slider.scrollLeft = this.currentScrollPosition - deltaX;
			}
		});

		// Buttons & Pagination
		this.bindButtons();
		this.renderPagination();
		this.initIntersectionObserver();
		this.slider.addEventListener('scroll', () => this.updateState(), { passive: true });

		// Erste Berechnung
		this.updateState();

		// Fix gegen Cache/Bild-Ladeprobleme
		// Update bei window load
		window.addEventListener('load', () => this.updateState());

		// ResizeObserver, falls Bilder die Größe nachladen
		const ro = new ResizeObserver(() => this.updateState());
		ro.observe(this.slider);

		// Bilder, die evtl. schon geladen sind
		const images = Array.from(this.slider.querySelectorAll('img'));
		let loadedCount = 0;
		images.forEach((img) => {
			if (img.complete) {
				loadedCount++;
			} else {
				img.addEventListener('load', () => {
					loadedCount++;
					if (loadedCount === images.length) this.updateState();
				});
			}
		});
		if (loadedCount === images.length) this.updateState();
		// ---------------------------------------------------------
	}

	private initIntersectionObserver() {
		this.intersectionObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.intersectionRatio >= 0.7) {
						entry.target.classList.remove('is-dimmed');
					} else {
						entry.target.classList.add('is-dimmed');
					}
				});
			},
			{
				root: this.slider,
				threshold: [0, 0.7, 1],
			}
		);

		this.slides.forEach((slide) => {
			this.intersectionObserver!.observe(slide);
		});
	}

	private resetMouseState() {
		this.addCssScrollProperties();
		this.isMouseDown = false;
		this.dragStart = null;
	}

	private bindButtons() {
		if (!this.sliderNavigationButtons) {
			return;
		}

		this.prevBtn = this.sliderNavigationButtons.querySelector('.scroll-slider__prev') as HTMLButtonElement | null;
		this.nextBtn = this.sliderNavigationButtons.querySelector('.scroll-slider__next') as HTMLButtonElement | null;

		this.prevBtn?.addEventListener('click', () => this.prevSlide());
		this.nextBtn?.addEventListener('click', () => this.nextSlide());
	}

	private renderPagination() {
		if (!this.paginationEl) {
			return;
		}

		this.paginationEl.innerHTML = '';
		this.paginationDots = [];

		this.slides.forEach((_, index) => {
			const dot = document.createElement('button');
			dot.type = 'button';
			dot.className = 'scroll-slider__dot';
			dot.setAttribute('aria-label', `Slide ${index + 1}`);
			dot.addEventListener('click', () => this.goToIndex(index));
			this.paginationEl!.appendChild(dot);
			this.paginationDots.push(dot);
		});
	}

	private getCurrentIndex(): number {
		const left = this.slider.scrollLeft;
		let idx = 0;
		let best = Number.POSITIVE_INFINITY;
		this.slides.forEach((el, i) => {
			const dist = Math.abs(el.offsetLeft - left);
			if (dist < best) {
				best = dist;
				idx = i;
			}
		});
		return idx;
	}

	private goToIndex(index: number) {
		if (!this.slides.length) {
			return;
		}
		const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
		const targetLeft = this.slides[clamped].offsetLeft;
		this.slider.style.scrollBehavior = 'smooth';
		this.slider.scrollTo({
			left: targetLeft,
			top: 0,
			behavior: 'smooth',
		});
	}

	nextSlide() {
		this.goToIndex(this.getCurrentIndex() + 1);
	}

	prevSlide() {
		this.goToIndex(this.getCurrentIndex() - 1);
	}

	private updateState() {
		if (!this.slides.length) {
			return;
		}

		const canScroll = this.slider.scrollWidth > this.slider.clientWidth;

		if (this.sliderNavigationButtons) {
			this.sliderNavigationButtons.style.display = canScroll ? '' : 'none';
		}
		if (this.paginationEl) {
			this.paginationEl.style.display = canScroll ? '' : 'none';
		}

		if (!canScroll) {
			this.slides.forEach((slide) => slide.classList.remove('is-active', 'is-prev', 'is-next'));
			if (this.slides[0]) {
				this.slides[0].classList.add('is-active');
			}
			return;
		}

		const idx = this.getCurrentIndex();

		if (this.prevBtn && this.nextBtn) {
			this.prevBtn.disabled = idx <= 0;
			this.nextBtn.disabled = idx >= this.slides.length - 1;
		}

		this.slides.forEach((slide) => {
			slide.classList.remove('is-active', 'is-prev', 'is-next');
		});
		this.slides[idx].classList.add('is-active');

		if (idx - 1 >= 0) {
			this.slides[idx - 1].classList.add('is-prev');
		}

		if (idx + 1 < this.slides.length) {
			this.slides[idx + 1].classList.add('is-next');
		}

		this.paginationDots.forEach((dot, i) => {
			dot.classList.toggle('is-active', i === idx);
		});
	}

	private addCssScrollProperties() {
		this.slider.style.scrollSnapType = 'x mandatory';
		this.slider.style.scrollBehavior = 'smooth';
		this.slider.style.cursor = 'default';
	}

	private clearCssScrollProperties() {
		this.slider.style.scrollSnapType = '';
		this.slider.style.scrollBehavior = '';
		this.slider.style.cursor = '';
	}
}
